import { memo, FC, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { showNotification } from 'config/toast';
import { LINKS } from 'constants/routes';
import { useTranslationEditorContext } from 'providers/TranslationEditor';
import { subscriptionApi } from 'api/subscription';
import useTouchHandleChange from 'hooks/useTouchHandleChange';
import { recoverPasswordValidationSchema } from 'components/Auth/validation';
import InputEmail from 'components/ui/InputEmail';
import { useRouter } from 'next/router';

import { Text, FormContainer, ButtonWrapper, InputLabel, Link, SubmitButton } from './styled';

type CancelSubscriptionProps = {
  onSent: (active: boolean) => void;
};

const CancelSubscription: FC<CancelSubscriptionProps> = ({ onSent }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { getDataAttributes } = useTranslationEditorContext();

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: recoverPasswordValidationSchema,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const touchHandleChange = useTouchHandleChange(formik);

  const helperText = useMemo(() => {
    return {
      text: formik.touched.email && formik.errors.email && t(formik.errors.email),
      attibutes: formik.touched.email && formik.errors.email ? getDataAttributes(formik.errors.email) : {},
    };
  }, [formik.errors.email, formik.touched.email, getDataAttributes, t]);

  const goToContactUs = useCallback(() => {
    router.push(LINKS.CONTACT_US);
  }, [router]);

  const handleSentRequest = useCallback(
    async (email: string) => {
      try {
        const response = await subscriptionApi.cancelPublicSubscriptionRequest(email);
        if (response) {
          onSent(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        showNotification(
          <span {...getDataAttributes('qr.page.cancel.subscription.pop.up.failed')}>
            {t('qr.page.cancel.subscription.pop.up.failed', 'Your subscription is already canceled')}
          </span>,
          'error',
          undefined,
          'qr.page.cancel.subscription.pop.up.failed'
        );
      }
    },
    [getDataAttributes, onSent, t]
  );

  const handleSubmit = useCallback(() => {
    if (formik.values.email && !formik.errors.email) {
      handleSentRequest(formik.values.email);
    }
    formik.resetForm();
  }, [formik, handleSentRequest]);

  return (
    <>
      <Text {...getDataAttributes('qr.cancelSubscription.text1')}>
        {t(
          'qr.cancelSubscription.text1',
          "You can cancel your subscription instantly. Just provide the email address you used when registering and click on the “cancel subscription” button below. It's as easy as that!"
        )}
      </Text>
      <InputLabel {...getDataAttributes('qr.cancelSubscription.emailLabel')} htmlFor="email">
        {t('qr.cancelSubscription.emailLabel', 'Email used for registration')}
      </InputLabel>
      <FormContainer>
        <InputEmail
          id="email"
          name="email"
          autoComplete="email"
          placeholder={t('qr.cancelSubscription.emailPlaceholder', 'email@gmail.com')}
          value={formik.values.email}
          onChange={touchHandleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={<span {...helperText.attibutes}>{helperText.text}</span>}
          inputProps={getDataAttributes('qr.cancelSubscription.emailPlaceholder', 'placeholder')}
        />
        <ButtonWrapper>
          <SubmitButton onClick={handleSubmit} {...getDataAttributes('qr.cancelSubscription.cancelButton')}>
            {t('qr.cancelSubscription.cancelButton', 'Cancel Subscription')}
          </SubmitButton>
        </ButtonWrapper>
      </FormContainer>
      <Text {...getDataAttributes('qr.cancelSubscription.text3_v2')}>
        <Trans
          i18nKey={'qr.cancelSubscription.text3_v2'}
          components={{
            LinkCSTeam: <Link onClick={goToContactUs} />,
          }}
        />
      </Text>
      <Text {...getDataAttributes('qr.cancelSubscription.text4')}>
        {t(
          'qr.cancelSubscription.text4',
          `You can also cancel your subscription by logging into you account>settings>subscription and clicking "cancel subscription"`
        )}
      </Text>
    </>
  );
};

export default memo(CancelSubscription);
